import React, { useCallback, useEffect } from "react";
import { OpponentInfo } from "./OpponentInfo";
import { Title } from "./Title"
import { GameClient, ImmuneCellInspection } from "../gameClient";

declare var gameClient: GameClient;

/** @jsx jsx */
/* @ts-ignore */
import { jsx, css } from '@emotion/react';
import { CellDescription } from "./cellDescription";
import { MutationModel } from "./MutationModal";
import { coloredSpritesheet } from "../rendering/spriteFactory";
import { LobbyScreen } from "./Screens/LobbyScreen";
import { ShopScreen } from "./Screens/ShopScreen";
import { NextWaveInfo } from "./NextWaveInfo";
import { GameEndScreen } from "./Screens/GameEndScreen";
import { Colors } from "../palettes/colorPalette";
import { SettingsButton } from "./SettingsButton";
import { SettingsPanel } from "./SettingsPanel";
import { PauseButton } from "./PauseButton";
import { CreditsPanel } from "./CreditsPanel";
import { MixerPanel } from "./MixerPanel";
import { RewardChooser } from "./RewardChooser";
import { Camera, CameraZoomLevel, Resolution, SimulationConstants } from "../../../shared/constants";
import { HowToPlayButton } from "./HowToPlayButton";
import { TutorialTooltip } from "./TutorialTooltip";
import { TutorialId, TutorialPositions, TutorialStep, TutorialStrings, allTutorials } from "./TooltipInfo";
import { ZoomWidget } from "./ZoomWidget";
import { ScreenOverlay } from "./ScreenOverlay";
import { getIngameTutorial } from "../userSettings";
import { TrainingLevelModal } from "./TrainingLevelModal";
import { Composition } from "../../../shared/composition";
import { GameElement } from "../../../shared/gameElementTypes";
import { Player } from "../../../shared/player";
import { TiebreakerModal } from "./TiebreakerModel";
import { RewardDisplay } from "./RewardDisplay";
import { TrainingLevel } from "../../../shared/playerAccount";
import { GameMode } from "../../../shared/game";
import { UIButton, UIButtonSmall } from "../styles/ButtonStyle";
import { IngameTutorialData } from "../tutorialGraph";
import { OpponentNameHeader } from "./OppenentNameHeader";
import { CrashModal } from "./CrashModal";

const containerStyle = css`
	user-select:none;
	overflow: hidden;
	width: 100vw; 
	height: 56.25vw; /* height:width ratio = 9/16 = .5625  */
	max-height: 100vh;
	max-width: 177.78vh; /* 16/9 = 1.778 */
	margin: auto;
	position: absolute;
	top:0;
	bottom:0;
	left:0;
	right:0;
	z-index: 1;
	color: ${Colors.UITextAndBorders};
`;

const upperLeftContainerStyle = css`
	width: fit-content;
	margin-top: 1em;
	margin-left: 1em;
	position: absolute;
	top: 0;
	left: 0;
	display: flex;
	z-index: 13; //above tutorial
`;

const lowerLeftContainerStyle = css`
	width: fit-content;
	margin-bottom: 1em;
	margin-left: 1em;
	position: absolute;
	bottom: 0;
	left: 0;
	display: flex;
`;

const controlsContainerStyle = css`
	position: absolute;
	top: 8vh;
	right: 0;
	display: flex;
	flex-direction: column;
	color: ${Colors.UIBackground}66;
	text-align: right;
	padding: 1vh;
	background-color: ${Colors.UIDescriptionText};

	@media (height < 900px) {
		top: 10vh;
	}
`;

type UserInterfaceProps = {
	gameClient: GameClient;
	round: number;
	opponents: [number, number, number, string, number, number][];
	maxFunds: number;
	currentFunds: number;
	maxCapacity: number;
	cells: Composition;
	hp: number;
	experience: number;
	level: number;
	offer: GameElement[];
	rewardOffer: GameElement[];
	rewardOptions: GameElement[];
	winners: Player[];
	inspectedImmuneCell: ImmuneCellInspection | null,
	gainedMoney: number;
	zoomLevel: number;
	draggedImmuneCell: boolean;
	hideMarket: boolean;
	trainingUpdated: boolean;
	enabledButtons?: string;
	hostId?: string;
	isSpawnGame: boolean;
	peerConnected: boolean;
};

export const UserInterface = ({
	gameClient,
	round,
	opponents,
	maxFunds,
	currentFunds,
	maxCapacity,
	cells,
	hp,
	experience,
	level,
	offer,
	rewardOffer,
	rewardOptions,
	winners,
	inspectedImmuneCell,
	gainedMoney,
	zoomLevel,
	draggedImmuneCell,
	hideMarket,
	trainingUpdated,
	enabledButtons,
	hostId,
	isSpawnGame,
	peerConnected
}: UserInterfaceProps) => {
	const currentState = gameClient.getCurrentState();
	const [maxRounds, setMaxRounds] = React.useState(SimulationConstants.MaxRoundsByTrainingLevel[TrainingLevel.NewPlayer]);
	const [showSettings, setShowSettings] = React.useState(false);
	const [popupTutorials, setPopupTutorials] = React.useState<TutorialId[]>([]);
	const [tutorials, setTutorials] = React.useState<TutorialId[]>([]);
	const [showMixer, setShowMixer] = React.useState(false);
	const [showCredits, setShowCredits] = React.useState(false);
	const [showMutation, setShowMutation] = React.useState(false);
	const [showTraining, setShowTraining] = React.useState(true);
	const [curtainOpacity, setCurtainOpacity] = React.useState(1);
	const [curtainFadeSpeed, setCurtainFadeSpeed] = React.useState(1);
	const [shouldStartFade, setShouldStartFade] = React.useState(false);

	const closeTutorials = () => {
		setPopupTutorials([]);
	}

	const toggleCredits = () => {
		setShowCredits(!showCredits);
	}

	const closeSettings = () => {
		setShowSettings(false);
	}

	//Only battles pause, the shop has nothing running to stop
	React.useEffect(() => {
		if (!showSettings || currentState !== "simulation") return;
		gameClient.pause();
		return () => gameClient.unpause();
	}, [showSettings, currentState]);

	const closeTraining = () => {
		setShowTraining(false);
	}

	const startGame = (mode: GameMode, continueGame?: string | null) => {
		// Cut to black immediately, then trigger fade
		setCurtainFadeSpeed(0);
		setCurtainOpacity(1);
		setShouldStartFade(true);
		gameClient.sendStartGame(mode, continueGame);
		setShowTraining(true);
	};

	// Effect to handle the fade after curtain goes black
	useEffect(() => {
		if (shouldStartFade) {
			requestAnimationFrame(() => { //make sure at least one render cycle happens
				setCurtainFadeSpeed(Camera.StartGameZoomOutTotalDurationSeconds);
				setCurtainOpacity(0);
				setShouldStartFade(false);
			});
		}
	}, [shouldStartFade]);

	// TODO: do this calculation somewhere in the actual game
	const length = cells.length || 0;
	const capacity = maxCapacity || 1;
	const numCells = length;
	const full = numCells >= capacity;

	const showMutationModel = () => {
		setShowMutation(true);
	}

	const clearMutationModel = () => {
		setShowMutation(false);
	}

	useEffect(() => {
		setMaxRounds(SimulationConstants.MaxRoundsByTrainingLevel[gameClient.trainingLevel]);
	}, [round]);

	const inspectionOnLeft = gameClient.isInspectionBlockingCell();

	const [tutorialData, setTutorialData] = React.useState<IngameTutorialData | undefined>();

	useEffect(() => {
		if (getIngameTutorial()) {
			setTutorialData(gameClient.tutorialStateMachine.getStep());
		}
	}, [gameClient.tutorialStateMachine.currentStep, gameClient.tutorialStateMachine.spotlightPos]);

	const nextTutorial = useCallback(() => {
		gameClient.tutorialStateMachine.onClick();
		setTutorialData(gameClient.tutorialStateMachine.getStep());
	}, []);

	return (
		<div css={containerStyle} className="ui mousesource" id="user-interface">
			{
				tutorials.length <= 0 && (currentState === "recap" || currentState === "simulation" || currentState === "shop") &&
				<>
					<ZoomWidget zoomLevel={zoomLevel} />
				</>
			}

			{
				(currentState !== "lobby" && currentState !== "end") &&
				<>
					<Title currentState={currentState} round={round} />
					<OpponentInfo playerId={gameClient.playerID} opponents={opponents} hide={zoomLevel === CameraZoomLevel.Normal && hideMarket} />
					<NextWaveInfo showMutationModel={showMutationModel} clearMutationModel={clearMutationModel} />
					{gameClient.roundRewardDisplay !== null && <RewardDisplay reward={gameClient.roundRewardDisplay}></RewardDisplay>}
				</>
			}

			{
				currentState !== "lobby" &&
				<div css={upperLeftContainerStyle}>
					<SettingsButton showSettings={setShowSettings} />
					{/* <HowToPlayButton toggleHowToPlay={setTutorials} /> */}
				</div>
			}

			{
				currentState === "simulation" &&
				<div css={lowerLeftContainerStyle}>
					<PauseButton />
				</div>
			}

			{
				(currentState === "shop" || gainedMoney !== 0) &&
				<ShopScreen
					round={round}
					draggedCell={draggedImmuneCell}
					capacity={capacity}
					currentFunds={currentFunds}
					experience={experience}
					full={full}
					length={length}
					level={level}
					maxFunds={maxFunds}
					numCells={numCells}
					offer={offer}
					rewardOffer={rewardOffer}
					gainedMoney={gainedMoney}
					currentState={currentState}
					showTutorial={setTutorials}
					enabledButtons={enabledButtons}
				/>
			}

			{
				currentState === "lobby" &&
				<LobbyScreen
					showSettings={setShowSettings}
					startGame={startGame}
					showCredits={toggleCredits}
					showTutorial={setTutorials}
					setCurtainOpacity={setCurtainOpacity}
					setCurtainFadeSpeed={setCurtainFadeSpeed}
					isSpawnGame={isSpawnGame}
					hostId={hostId}
					peerConnected={peerConnected}
				/>
			}

			{
				rewardOptions && rewardOptions.length > 0 && <RewardChooser choices={rewardOptions} />
				//gameClient.getCurrentState() === "shop" && < RewardChooser hide={toggleReward} choose={() => { }} choices={rewardOffer} />
			}

			{
				showMixer && <MixerPanel showMixer={setShowMixer} />
			}

			{
				showCredits && <CreditsPanel dismiss={toggleCredits} />
			}

			{
				currentState === "end" &&
				<GameEndScreen round={round} maxRounds={maxRounds} />
			}

			{
				(currentState !== "end") && gameClient.inspection &&
				<div style={{ marginTop: "3%", marginLeft: inspectionOnLeft ? "48%" : "2%", position: "relative", zIndex: 10 }}>
					<CellDescription
						compElement={gameClient.inspection?.compElement}
						element={gameClient.inspection?.element}
						showTooltips={gameClient.inspection.hideTooltips ? false : true}></CellDescription>
				</div>
			}

			{
				gameClient.showOpponentName !== "" && <OpponentNameHeader opponentName={gameClient.showOpponentName}></OpponentNameHeader>
			}

			{currentState === "lobby" && tutorials.length <= 0 && trainingUpdated && showTraining && <TrainingLevelModal fullscreen={false} onClose={closeTraining} />}

			{
				showSettings && <SettingsPanel onClose={closeSettings} isOpen={showSettings} setShowMixer={setShowMixer} showCredits={setShowCredits} />
			}

			{
				(showMutation || gameClient.showVirusGrowthDisplay && coloredSpritesheet) && gameClient.latestVirusGrowth &&
				< MutationModel change={gameClient.latestVirusGrowth}></MutationModel>
			}
			{
				gameClient.showTiebreakerAnnouncement && <TiebreakerModal tiebreakerNumber={gameClient.tiebreakerRound}></TiebreakerModal>
			}


			{popupTutorials.length > 0 && <TutorialTooltip tutorials={popupTutorials} fullscreen={false} onClose={closeTutorials} />}
			{tutorialData !== undefined && <ScreenOverlay tutorialStepData={tutorialData} nextTutorial={nextTutorial} requireClick={tutorialData.requireClick} />}
			{tutorialData !== undefined && gameClient.tutorialStateMachine.isVisible() &&
				<UIButtonSmall css={{ position: "absolute", top: "50%", right: "20px", zIndex: 12, transform: "translate(0%, -50%)", }} onClick={() => {
					gameClient.tutorialStateMachine.onSkipToGameplay();
				}}>Skip To Gameplay</UIButtonSmall>}
			{tutorials.length > 0 && <TutorialTooltip tutorials={tutorials} fullscreen={false} onClose={closeTutorials} />}
			<div id="loading-curtain" style={{
				position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 12,
				background: `#000000`,
				transition: `opacity ${curtainFadeSpeed}s`,
				opacity: curtainOpacity,
				pointerEvents: curtainOpacity === 1 ? "auto" : "none",
			}}>
			</div>
			{
				gameClient.crashReport !== null &&
				<CrashModal
					reportText={gameClient.crashReport.text}
					onReloadSave={gameClient.crashReport.canReloadSave ? () => gameClient.leaveCrashedGame(true) : undefined}
					onBackToMenu={() => gameClient.leaveCrashedGame(false)}
				/>
			}
		</div >

	)
};
